import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Check, ExternalLink, LayoutDashboard, Truck, UserRound, ScanLine, Globe } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { setBusinessTimezone } from "../lib/utils";
import { OutletInfoCard } from "../components/settings/OutletInfoCard";
import { DeliveryZoneCard } from "../components/settings/DeliveryZoneCard";
import { DeliveryDaysCard } from "../components/settings/DeliveryDaysCard";
import { StartDaysCard } from "../components/settings/StartDaysCard";
import { DriverMapCard } from "../components/settings/DriverMapCard";
import { TaxSettingsCard } from "../components/settings/TaxSettingsCard";
import { DataImportCard } from "../components/settings/DataImportCard";
import { EraseDataCard } from "../components/settings/EraseDataCard";

const ORIGIN = window.location.origin;

const APPS = [
  {
    key: "admin", label: "Admin Dashboard", icon: LayoutDashboard, path: "/login",
    description: "Operations dashboard for staff — customers, subscriptions, routes, payments.",
  },
  {
    key: "driver", label: "Driver App", icon: Truck, path: "/driver/login",
    description: "Installable app drivers use to see today's routes and mark deliveries.",
  },
  {
    key: "customer", label: "Customer Portal", icon: UserRound, path: "/customer",
    description: "Share with customers to sign up, check their plan/QR code, or track deliveries.",
  },
  {
    key: "pos", label: "QR-POS (Staff)", icon: ScanLine, path: "/pos/login",
    description: "Counter-tablet app staff use to scan a pickup customer's QR code.",
  },
];

function AppLinkCard({ app }) {
  const url = `${ORIGIN}${app.path}`;
  const [copied, setCopied] = useState(false);
  const Icon = app.icon;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — link is still selectable/visible */ }
  };

  return (
    <Card data-testid={`settings-app-link-card-${app.key}`}>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">{app.label}</p>
            <p className="mt-0.5 text-xs text-text-muted">{app.description}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <div className="flex-shrink-0 rounded-md border border-border bg-white p-2">
            <QRCodeSVG value={url} size={72} data-testid={`settings-app-link-qr-${app.key}`} />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="truncate rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-text-muted" data-testid={`settings-app-link-url-${app.key}`}>
              {url}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={copy} data-testid={`settings-app-link-copy-${app.key}`}>
                {copied ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
                {copied ? "Copied" : "Copy Link"}
              </Button>
              <a href={url} target="_blank" rel="noreferrer" data-testid={`settings-app-link-open-${app.key}`}>
                <Button variant="outline" size="sm"><ExternalLink size={14} strokeWidth={1.5} /></Button>
              </a>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TimezoneCard() {
  const [state, setState] = useState({ loading: true, timezone: "", supported: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/outlet/timezone")
      .then(({ data }) => setState({ loading: false, timezone: data.timezone, supported: data.supported }))
      .catch(() => setState({ loading: false, timezone: "America/Edmonton", supported: [] }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/outlet/timezone", { timezone: state.timezone });
      setBusinessTimezone(data.timezone);
      toast.success("Business timezone updated.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not update the timezone."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-timezone-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Globe size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Business Timezone</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Every "today" calculation across the app — dashboard KPIs, delivery scheduling, ETA windows — is anchored to this timezone, not the viewer's own device.
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <Select
            value={state.timezone}
            onValueChange={(v) => setState((s) => ({ ...s, timezone: v }))}
            disabled={state.loading}
          >
            <SelectTrigger data-testid="settings-timezone-select" className="max-w-xs">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {state.supported.map((tz) => (
                <SelectItem key={tz.value} value={tz.value} data-testid={`settings-timezone-option-${tz.value}`}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleSave} disabled={saving || state.loading} data-testid="settings-timezone-save-button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const [outlet, setOutlet] = useState(null);

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Settings"
        description="System-wide settings — share these links so anyone can install the matching app on their phone."
      />
      <Tabs defaultValue="general">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
          <TabsTrigger value="delivery-days" data-testid="settings-tab-delivery-days">Delivery Days</TabsTrigger>
          <TabsTrigger value="apps" data-testid="settings-tab-apps">Installable Apps</TabsTrigger>
          <TabsTrigger value="data-import" data-testid="settings-tab-data-import">Data Import</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="mt-4">
            <h2 className="mb-1 font-heading text-sm font-semibold uppercase tracking-wide text-text-muted">Business Settings</h2>
            <p className="mb-4 text-sm text-text-muted">Controls that affect every portal (admin, driver, customer).</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
              <TimezoneCard />
              <OutletInfoCard onOutletLoaded={setOutlet} />
              <TaxSettingsCard />
            </div>
            <div className="mt-4">
              <DeliveryZoneCard outlet={outlet} />
            </div>
            <div className="mt-4">
              <DriverMapCard />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="delivery-days">
          <div className="mt-4 max-w-2xl space-y-4">
            <DeliveryDaysCard />
            <StartDaysCard />
          </div>
        </TabsContent>

        <TabsContent value="apps">
          <div className="mt-4">
            <h2 className="mb-1 font-heading text-sm font-semibold uppercase tracking-wide text-text-muted">Installable Apps</h2>
            <p className="mb-4 text-sm text-text-muted">
              Scan a QR code with a phone camera to open the link, then use the browser's "Add to Home Screen" /
              "Install App" option to install it — no app store needed.
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
              {APPS.map((app) => <AppLinkCard key={app.key} app={app} />)}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="data-import">
          <div className="mt-4 max-w-2xl">
            <h2 className="mb-1 font-heading text-sm font-semibold uppercase tracking-wide text-text-muted">Data Import</h2>
            <p className="mb-4 text-sm text-text-muted">
              Restore or migrate the whole system from a MySQL/MariaDB <code>.sql</code> dump. Use this to import a fresh
              backup — the same file you'd upload here also works in production.
            </p>
            <DataImportCard />
            <div className="mt-8">
              <h2 className="mb-1 font-heading text-sm font-semibold uppercase tracking-wide text-danger">Danger Zone</h2>
              <p className="mb-4 text-sm text-text-muted">Irreversible actions. Please be certain.</p>
              <EraseDataCard />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
